import React, { useContext, useEffect, useState } from "react"
import { Link } from "gatsby"
import { CartContext } from "./layout"

const ExactNavLink = ({ suppressActive, ...props }) => (
  <Link
    getProps={({ isCurrent }) => {
      return (isCurrent && !suppressActive) ? { className: "nav-link active" } : { className: "nav-link" }
    }}
    {...props}
  />
)

const Navbar = () => {
  const { cart, showCart } = useContext(CartContext)
  const [isClient, setClient] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [navOpen, setNavOpen] = useState(false)
  const [productsOpen, setProductsOpen] = useState(false)

  useEffect(() => {
    setClient(true)
    const handleScroll = () => {
      const isScrolled = window.scrollY > 60
      setScrolled(isScrolled)
      document.body.classList.toggle('page-scrolled', isScrolled)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  let cartCount = 0
  if (Array.isArray(cart)) {
    cart.forEach(item => { cartCount += (parseInt(item?.quantity, 10) || 1) })
  }

  const CartButton = () => (
    <button className="cart-btn" onClick={() => showCart()} aria-label="Open cart">
      <i className="bi bi-cart2" style={{ fontSize: '24px' }}></i>
      {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
    </button>
  )

  const handleLinkClick = () => {
    setNavOpen(false)
    setProductsOpen(false)
  }

  return (
    <nav className={`navbar navbar-expand-lg navbar-light bg-white fixed-top${scrolled ? ' navbar-scrolled' : ''}`}>
      <div className="container flex-column position-relative">

        {/* Row 1: Toggler (mobile-left) | Logo (centered) | Cart (mobile-right) */}
        <div className="navbar-row-top position-relative d-flex align-items-center justify-content-center w-100">
          <button
            className="navbar-toggler position-absolute start-0 d-lg-none d-flex align-items-center justify-content-center"
            type="button"
            onClick={() => setNavOpen(!navOpen)}
            aria-expanded={navOpen}
            aria-label="Toggle navigation"
            style={{ width: '44px', height: '44px', border: 'none', background: 'transparent' }}
          >
            <i className={`bi ${navOpen ? 'bi-x-lg' : 'bi-list'}`} style={{ fontSize: '26px', color: '#ffffff' }}></i>
          </button>

          <Link to="/" className="navbar-brand m-0" onClick={handleLinkClick}>
            <img src="/logo.png" alt="plumes" /><sup style={{ fontSize: '12px', top: '-15px', left: '-2px', color: '#89b559' }}>&nbsp;®</sup>
          </Link>

          {/* Cart visible on all screens */}
          {isClient && (
            <div className="cart-wrap position-absolute end-0 top-50 translate-middle-y">
              <CartButton />
            </div>
          )}
        </div>

        {/* Row 2: Nav links centered */}
        <div className={`collapse navbar-collapse ${navOpen ? 'show' : ''}`} id="main-navbar">
          <ul className="navbar-nav mx-auto">
            <li className="nav-item">
              <ExactNavLink to="/" onClick={handleLinkClick} suppressActive={productsOpen}>
                Home
              </ExactNavLink>
            </li>

            {/* Products Dropdown / Desktop Megamenu */}
            <li className={`nav-item dropdown dropdown-mega ${productsOpen ? 'show' : ''}`}>
              <button
                className={`nav-link dropdown-toggle bg-transparent border-0 text-start d-flex justify-content-between align-items-center w-100 ${productsOpen ? 'active' : ''}`}
                onClick={() => setProductsOpen(!productsOpen)}
                aria-expanded={productsOpen}
                id="products-menu"
              >
                <span>Products</span>
              </button>

              <div className={`dropdown-menu megamenu-content ${productsOpen ? 'show' : ''}`} aria-labelledby="products-menu">
                <div className="container-fluid px-lg-4 py-lg-3">
                  <div className="row g-3 g-lg-4">

                    {/* Col 1: Mattresses */}
                    <div className="col-12 col-lg-3 megamenu-col">
                      <Link to="/products/mattresses" className="megamenu-heading d-flex align-items-center" onClick={handleLinkClick}>
                        <i className="bi bi-layers text-primary me-2 fs-5"></i>
                        <span>Mattresses</span>
                      </Link>
                      <p className="megamenu-subtext d-none d-lg-block">Ergonomic support & cloud comfort</p>
                      <ul className="megamenu-list list-unstyled mb-0">
                        <li>
                          <Link to="/products/flos" className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Flos Mattress</span>
                            <span className="item-badge badge-soft">Cloud Soft</span>
                          </Link>
                        </li>
                        <li>
                          <Link to="/products/aura" className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Aura Hybrid</span>
                            <span className="item-badge badge-spring">Pocket Spring</span>
                          </Link>
                        </li>
                        <li>
                          <Link to="/products/vita" className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Vita Orthopedic</span>
                            <span className="item-badge badge-ortho">Back Support</span>
                          </Link>
                        </li>
                        <li>
                          <Link to="/products/clarus" className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Clarus CoolGel</span>
                            <span className="item-badge badge-cool">Cooling Gel</span>
                          </Link>
                        </li>
                        <li>
                          <Link to="/products/pax" className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Pax Latex</span>
                            <span className="item-badge badge-latex">100% Natural</span>
                          </Link>
                        </li>
                        <li>
                          <Link to="/products/futon" className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Futon Mattress</span>
                            <span className="item-badge badge-futon">Dual-Sided</span>
                          </Link>
                        </li>
                      </ul>
                    </div>

                    {/* Col 2: Pillows & Accessories */}
                    <div className="col-12 col-lg-3 megamenu-col">
                      <Link to="/products/pillows" className="megamenu-heading d-flex align-items-center" onClick={handleLinkClick}>
                        <i className="bi bi-cloud-moon text-primary me-2 fs-5"></i>
                        <span>Pillows & Comfort</span>
                      </Link>
                      <p className="megamenu-subtext d-none d-lg-block">Optimal head & neck alignment</p>
                      <ul className="megamenu-list list-unstyled mb-0">
                        <li>
                          <Link to="/products/pillows" state={{ type: 0 }} className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Memory Foam Pillow</span>
                            <span className="item-badge badge-offer">Up to 50% Off</span>
                          </Link>
                        </li>
                        <li>
                          <Link to="/products/pillows" state={{ type: 1 }} className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Latex Soft Pillow</span>
                            <span className="item-badge badge-latex">Organic</span>
                          </Link>
                        </li>
                        <li>
                          <Link to="/products/pillows" state={{ type: 2 }} className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Latex Contour Pillow</span>
                            <span className="item-badge badge-ortho">Ergonomic</span>
                          </Link>
                        </li>
                        <li>
                          <Link to="/products/pillows" state={{ type: 3 }} className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Polyfiber Pillow</span>
                            <span className="item-badge badge-soft">Washable</span>
                          </Link>
                        </li>
                        <li>
                          <Link to="/products/eyemasks" className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Plumes Eye Mask</span>
                            <span className="item-badge badge-dark-pill">Blackout</span>
                          </Link>
                        </li>
                      </ul>
                    </div>

                    {/* Col 3: Bedding & Protection */}
                    <div className="col-12 col-lg-3 megamenu-col">
                      <span className="megamenu-heading d-flex align-items-center">
                        <i className="bi bi-shield-check text-primary me-2 fs-5"></i>
                        <span>Bedding & Care</span>
                      </span>
                      <p className="megamenu-subtext d-none d-lg-block">Protect & elevate your bedroom</p>
                      <ul className="megamenu-list list-unstyled mb-0">
                        <li>
                          <Link to="/products/protectors" className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Mattress Protector</span>
                            <span className="item-desc">Waterproof & anti-dust</span>
                          </Link>
                        </li>
                        <li>
                          <Link to="/products/bedspread" className="megamenu-item" onClick={handleLinkClick}>
                            <span className="item-title">Plumes Bedspread</span>
                            <span className="item-desc">Breathable luxury cotton</span>
                          </Link>
                        </li>
                        <li className="mt-3">
                          <Link to="/products/all" className="btn btn-sm btn-outline-info w-100 fw-bold py-2" onClick={handleLinkClick}>
                            View All Products <i className="bi bi-arrow-right ms-1"></i>
                          </Link>
                        </li>
                      </ul>
                    </div>

                    {/* Col 4: Highlight Card (Desktop Only) */}
                    <div className="col-12 col-lg-3 d-none d-lg-block">
                      <div className="megamenu-promo-card h-100 p-3 rounded d-flex flex-column justify-content-between">
                        <div>
                          <span className="badge bg-primary text-dark fw-bold mb-2">PLUMES PROMISE</span>
                          <h6 className="fw-bold text-dark mb-1">100-Night Free Trial</h6>
                          <p className="small text-muted mb-3">Sleep risk-free. If you don't fall in love, return it with 100% money back.</p>
                          <div className="d-flex align-items-center text-info small fw-bold mb-1">
                            <i className="bi bi-truck me-2"></i> Free Express Delivery
                          </div>
                          <div className="d-flex align-items-center text-info small fw-bold">
                            <i className="bi bi-patch-check me-2"></i> 10-Year Warranty
                          </div>
                        </div>
                        <Link to="/products/all" className="btn btn-info btn-sm w-100 mt-3 fw-bold" onClick={handleLinkClick}>
                          Shop Full Catalog
                        </Link>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </li>

            <li className="nav-item">
              <ExactNavLink to="/about" onClick={handleLinkClick} suppressActive={productsOpen}>
                Know Us
              </ExactNavLink>
            </li>
            <li className="nav-item">
              <ExactNavLink to="/features" onClick={handleLinkClick} suppressActive={productsOpen}>
                Why Plumes
              </ExactNavLink>
            </li>
          </ul>
        </div>

      </div>
    </nav>
  )
}

export default Navbar
